import LanguageSwitcher from "@/components/LanguageSwitcher";
import RealTimeSearch from "@/components/RealTimeSearch";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { useLanguage } from "@/hooks/useLanguage";
import {
  BookOpen,
  ChevronDown,
  Cpu,
  Facebook,
  Home,
  Instagram,
  Linkedin,
  Mail,
  MapIcon,
  MapPin,
  MessageCircle,
  Phone,
  Search,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/logo.svg";
import logoWhiteBg from "../assets/logo-white-bg.svg";

interface NavItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  key: string;
  isDropdown?: boolean;
}

// ECOSAFE Social Media Component
const EcosafeIcon = ({
  type,
}: {
  type: "twitter" | "whatsapp" | "tiktok" | "youtube" | "snapchat";
}) => {
  const icons = {
    twitter: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z" />
      </svg>
    ),
    whatsapp: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
      </svg>
    ),
    tiktok: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M9 0h1.98c.144.715.54 1.617 1.235 2.512C12.895 3.389 13.797 4 15 4v2c-1.753 0-3.07-.814-4-1.829V11a5 5 0 1 1-5-5v2a3 3 0 1 0 3 3z" />
      </svg>
    ),
    youtube: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.01 2.01 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.01 2.01 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31 31 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.01 2.01 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A100 100 0 0 1 7.858 2zM6.4 5.209v4.818l4.157-2.408z" />
      </svg>
    ),
    snapchat: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M15.943 11.526c-.111-.303-.323-.465-.564-.599a1 1 0 0 0-.123-.064l-.219-.111c-.752-.399-1.339-.902-1.746-1.498a3.4 3.4 0 0 1-.3-.531c-.034-.1-.032-.156-.008-.207a.3.3 0 0 1 .097-.1c.129-.086.262-.173.352-.231.162-.104.289-.187.371-.245.309-.216.525-.446.66-.702a1.4 1.4 0 0 0 .069-1.16c-.205-.538-.713-.872-1.329-.872a1.8 1.8 0 0 0-.487.065c.006-.368-.002-.757-.035-1.139-.116-1.344-.587-2.048-1.077-2.61a4.3 4.3 0 0 0-1.095-.881C9.764.216 8.92 0 7.999 0s-1.76.216-2.505.641c-.412.232-.782.53-1.097.883-.49.562-.96 1.267-1.077 2.61-.033.382-.04.772-.036 1.138a1.8 1.8 0 0 0-.487-.065c-.615 0-1.124.335-1.328.873a1.4 1.4 0 0 0 .067 1.161c.136.256.352.486.66.701.082.058.21.14.371.246l.339.221a.4.4 0 0 1 .109.11c.026.053.027.11-.012.217a3.4 3.4 0 0 1-.295.52c-.398.583-.968 1.077-1.696 1.472-.385.204-.786.34-.955.8-.128.348-.044.743.28 1.075q.18.189.409.31a4.4 4.4 0 0 0 1 .4.7.7 0 0 1 .202.09c.118.104.102.26.259.488q.12.178.296.3c.33.229.701.243 1.095.258.355.014.758.03 1.217.18.19.064.389.186.618.328.55.338 1.305.802 2.566.802 1.262 0 2.02-.466 2.576-.806.227-.14.424-.26.609-.321.46-.152.863-.168 1.218-.181.393-.015.764-.03 1.095-.258a1.14 1.14 0 0 0 .336-.368c.114-.192.11-.327.217-.42a.6.6 0 0 1 .19-.087 4.5 4.5 0 0 0 1.014-.404c.16-.087.306-.2.429-.336l.004-.005c.304-.325.38-.709.256-1.047m-1.121.602c-.684.378-1.139.337-1.493.565-.3.193-.122.61-.34.76-.269.186-1.061-.012-2.085.326-.845.279-1.384 1.082-2.903 1.082s-2.045-.801-2.904-1.084c-1.022-.338-1.816-.14-2.084-.325-.218-.15-.041-.568-.341-.761-.354-.228-.809-.187-1.492-.563-.436-.24-.189-.39-.044-.46 2.478-1.199 2.873-3.05 2.89-3.188.022-.166.045-.297-.138-.466-.177-.164-.962-.65-1.18-.802-.36-.252-.52-.503-.402-.812.082-.214.281-.295.49-.295a1 1 0 0 1 .197.022c.396.086.78.285 1.002.338q.04.01.082.011c.118 0 .16-.06.152-.195-.026-.433-.087-1.277-.019-2.066.094-1.084.444-1.622.859-2.097.2-.229 1.137-1.22 2.93-1.22 1.792 0 2.732.987 2.931 1.215.416.475.766 1.013.859 2.098.068.788.009 1.632-.019 2.065-.01.142.034.195.152.195a.4.4 0 0 0 .082-.01c.222-.054.607-.253 1.002-.338a1 1 0 0 1 .197-.023c.21 0 .409.082.49.295.117.309-.04.56-.401.812-.218.152-1.003.638-1.18.802-.184.169-.16.3-.139.466.018.14.413 1.991 2.89 3.189.147.073.394.222-.041.464" />
      </svg>
    ),
  };

  return icons[type];
};

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrollState, setScrollState] = useState(0); // 0 = at top, 1 = scrolled with blur, 2 = scrolled white
  const [showSearch, setShowSearch] = useState(false);
  const { t } = useTranslation("navigation");
  const { isRTL } = useLanguage();
  const location = useLocation();

  // Check if current page is home
  const isHomePage = location.pathname === "/";

  // Enhanced scroll effect with three states
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY === 0) {
        setScrollState(0); // At top - transparent, no blur
      } else if (scrollY <= 100) {
        setScrollState(1); // Small scroll - transparent with blur
      } else {
        setScrollState(2); // More scroll - white background
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const socialLinks = [
    {
      href: "https://www.linkedin.com/company/ecosafe-for-training-and-consultancy-services/",
      icon: Linkedin,
      label: isRTL ? "لينكد إن" : "LinkedIn",
      ariaLabel: isRTL
        ? "زيارة صفحة لينكد إن الخاصة بنا"
        : "Visit our LinkedIn page",
    },
    {
      href: "https://x.com/EcosafeT89238",
      icon: () => <EcosafeIcon type="twitter" />,
      label: isRTL ? "إكس (تويتر)" : "Twitter/X",
      ariaLabel: isRTL
        ? "زيارة صفحة إكس الخاصة بنا"
        : "Visit our X (Twitter) page",
    },
    {
      href: "https://www.facebook.com/p/Ecosafe-for-Training-and-Consulting-61565045387866/",
      icon: Facebook,
      label: isRTL ? "فيسبوك" : "Facebook",
      ariaLabel: isRTL
        ? "زيارة صفحة فيسبوك الخاصة بنا"
        : "Visit our Facebook page",
    },
    {
      href: "https://www.instagram.com/ecosafe.sa?igsh=MWQ0ZnNuYTVwbTYyOA==",
      icon: Instagram,
      label: isRTL ? "إنستغرام" : "Instagram",
      ariaLabel: isRTL
        ? "زيارة صفحة إنستغرام الخاصة بنا"
        : "Visit our Instagram page",
    },
    {
      href: "https://api.whatsapp.com/send/?phone=966115062744&text&type=phone_number&app_absent=0",
      icon: () => <EcosafeIcon type="whatsapp" />,
      label: isRTL ? "واتساب" : "WhatsApp",
      ariaLabel: isRTL ? "تواصل معنا عبر واتساب" : "Contact us via WhatsApp",
    },
    {
      href: "https://www.tiktok.com/@ecosafe.sa?_r=1&_t=ZS-92QhchidIRR",
      icon: () => <EcosafeIcon type="tiktok" />,
      label: isRTL ? "تيك توك" : "TikTok",
      ariaLabel: isRTL
        ? "زيارة صفحة تيك توك الخاصة بنا"
        : "Visit our TikTok page",
    },
    {
      href: "https://www.youtube.com/@Ecosafetech",
      icon: () => <EcosafeIcon type="youtube" />,
      label: isRTL ? "يوتيوب" : "YouTube",
      ariaLabel: isRTL
        ? "زيارة قناة يوتيوب الخاصة بنا"
        : "Visit our YouTube channel",
    },
    {
      href: "https://snapchat.com/t/k7anJiwq",
      icon: () => <EcosafeIcon type="snapchat" />,
      label: isRTL ? "سناب شات" : "Snapchat",
      ariaLabel: isRTL
        ? "زيارة قناة سناب شات الخاصة بنا"
        : "Visit our Snapchat page",
    },
  ];

  // Get header classes based on scroll state and page type
  const getHeaderClasses = () => {
    const positionClass = isHomePage ? "fixed" : "sticky";
    const baseClasses = `${positionClass} top-0 left-0 right-0 z-50 transition-all duration-500`;

    if (!isHomePage) {
      // Non-home pages: always white background with shadow
      return `${baseClasses} bg-white shadow-lg border-b border-ecosafe-green/20`;
    }

    // Home page behavior (original)
    switch (scrollState) {
      case 0: // At top - transparent, no blur
        return `${baseClasses} bg-transparent`;
      case 1: // Small scroll - transparent with blur
        return `${baseClasses} bg-transparent backdrop-blur-xl`;
      case 2: // More scroll - white background
        return `${baseClasses} bg-white shadow-xl border-b border-ecosafe-green/20`;
      default:
        return `${baseClasses} bg-transparent`;
    }
  };

  // Get text color classes based on page and scroll state
  const getTextColorClasses = () => {
    if (!isHomePage) {
      // Non-home pages: always dark text (since backgrounds are white)
      return "text-ecosafe-black";
    }
    // Home page: original behavior (white when transparent, dark when scrolled)
    return scrollState === 2 ? "text-ecosafe-black" : "text-white";
  };

  // Split navigation items - before and after Training Courses
  const navItemsBeforeTraining: NavItem[] = [
    {
      href: "/",
      label: isRTL ? "الرئيسية" : "Home",
      icon: Home,
      key: "home",
    },
    {
      href: "/about",
      label: isRTL ? "عن الشركة" : "About Us",
      icon: Users,
      key: "about",
    },
  ];

  const navItemsAfterTraining: NavItem[] = [
    {
      href: "/digital-solutions",
      label: isRTL ? "الحلول الرقمية" : "Digital Solutions",
      icon: Cpu,
      key: "digital-solutions",
    },
    {
      href: "/cities",
      label: isRTL ? "المدن" : "Cities",
      icon: MapIcon,
      key: "cities",
    },
    {
      href: "/contact",
      label: isRTL ? "تواصل معنا" : "Contact Us",
      icon: MessageCircle,
      key: "contact",
    },
  ];

  // Combined for mobile menu
  const allNavItems: NavItem[] = [
    ...navItemsBeforeTraining,
    {
      href: "/training-courses",
      label: isRTL ? "الدورات التدريبية" : "Training Courses",
      icon: BookOpen,
      key: "training-courses",
      isDropdown: true,
    },
    ...navItemsAfterTraining,
  ];

  return (
    <header className={getHeaderClasses()} dir={isRTL ? "rtl" : "ltr"}>
      {/* ECOSAFE Top Bar */}
      <div
        className={`bg-gradient-to-r from-ecosafe-black via-ecosafe-gray to-ecosafe-black text-white transition-all duration-500 ${
          !isHomePage || scrollState === 2 ? "py-1 opacity-95" : "py-3"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Contact Information */}
            <div
              className={`flex items-center transition-all duration-500 ${
                isRTL
                  ? "space-x-reverse space-x-4 md:space-x-6"
                  : "space-x-4 md:space-x-6"
              } ${
                !isHomePage || scrollState === 2
                  ? "opacity-0 scale-95 pointer-events-none"
                  : "opacity-100 scale-100"
              }`}
            >
              <a
                href="https://maps.app.goo.gl/jevE2aFgd1Rn2hxz5?g_st=aw"
                target="_blank"
                rel="noopener noreferrer"
                className={`hidden md:flex items-center text-xs sm:text-sm text-white/80 hover:text-white transition-colors duration-300 hover:scale-105 ${
                  isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                }`}
              >
                <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden lg:inline">
                  {isRTL
                    ? "المقر الرئيسي في المملكة العربية السعودية"
                    : "Head Quarter in Saudi Arabia"}
                </span>
                <span className="lg:hidden">
                  {isRTL ? "السعودية" : "Saudi Arabia"}
                </span>
              </a>
              <a
                href="mailto:info@ecosafe.com.sa"
                className={`hidden sm:flex items-center text-xs sm:text-sm text-white/80 hover:text-white transition-colors duration-300 hover:scale-105 ${
                  isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                }`}
              >
                <Mail className="h-3 w-3 mx-1 sm:h-4 sm:w-4" />
                <span>info@ecosafe.com.sa</span>
              </a>
              <a
                href="tel:+966115062744"
                className={`flex items-center text-xs sm:text-sm text-white/80 hover:text-white transition-colors duration-300 hover:scale-105 ${
                  isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                }`}
              >
                <Phone className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>{isRTL ? "2744 506 11 966+" : "+966 11 506 2744"}</span>
              </a>
            </div>

            {/* Social Media Links */}
            <div
              className={`flex items-center transition-all duration-500 ${
                isRTL
                  ? "space-x-reverse space-x-1 sm:space-x-2"
                  : "space-x-1 sm:space-x-2"
              } ${!isHomePage || scrollState === 2 ? "scale-90" : "scale-100"}`}
            >
              {socialLinks.map((social, index) => (
                <Button
                  key={index}
                  asChild
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 sm:h-8 sm:w-8 text-white/80 hover:text-white hover:bg-white/10 transition-all duration-300 hover:scale-110 rounded-full"
                >
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={social.label}
                    aria-label={social.ariaLabel}
                  >
                    <social.icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  </a>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-2 sm:px-4">
        <div
          className={`flex items-center justify-between transition-all duration-500 ${
            !isHomePage || scrollState === 2 ? "h-14 sm:h-16" : "h-16 sm:h-20"
          }`}
        >
          {/* Company Logo */}
          <Link
            to="/"
            className="flex items-center group flex-shrink-0"
            aria-label={
              isRTL
                ? "الصفحة الرئيسية - إيكوسيف للتدريب والاستشارات"
                : "Home - ECOSAFE Training and Consultancy"
            }
          >
            <img
              src={!isHomePage || scrollState === 2 ? logoWhiteBg : logo}
              width={595}
              height={260}
              alt={
                isRTL
                  ? "إيكوسيف للتدريب والاستشارات"
                  : "ECOSAFE Training and Consultancy"
              }
              className={`logo w-auto object-contain transition-all duration-500 group-hover:scale-105 ${
                !isHomePage || scrollState === 2
                  ? "h-12 sm:h-14 md:h-16"
                  : "h-14 sm:h-16 md:h-20"
              }`}
            />
          </Link>

          {/* Centered Navigation Menu */}
          <div
            className={`hidden lg:flex items-center justify-center flex-1 ${
              isRTL
                ? "space-x-reverse space-x-4 xl:space-x-8"
                : "space-x-4 xl:space-x-8"
            }`}
          >
            {/* Navigation items before Training Courses */}
            {navItemsBeforeTraining.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;

              return (
                <Link key={item.key} to={item.href}>
                  <Button
                    variant="ghost"
                    className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all duration-300 hover:bg-ecosafe-green/10 group ${
                      isActive
                        ? "text-ecosafe-green-dark bg-ecosafe-green/5"
                        : `${getTextColorClasses()} hover:text-ecosafe-green-dark`
                    }`}
                    aria-label={`${isRTL ? "انتقل إلى" : "Navigate to"} ${
                      item.label
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 transition-all duration-300 group-hover:scale-110 ${
                        isActive ? "text-ecosafe-green" : ""
                      }`}
                    />
                    <span className="text-xs font-medium">{item.label}</span>
                  </Button>
                </Link>
              );
            })}

            {/* Training Courses Dropdown Menu - Fixed to show icon consistently */}
            <NavigationMenu className="[&_ul]:list-none [&_li]:list-none">
              <NavigationMenuList>
              <NavigationMenuItem className="list-none">
                <NavigationMenuTrigger
                  className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all duration-300 bg-transparent hover:bg-ecosafe-green/10 border-0 h-auto ${getTextColorClasses()} hover:text-ecosafe-green-dark data-[state=open]:bg-ecosafe-green/5 group focus:bg-transparent data-[state=open]:text-ecosafe-green-dark [&>svg]:hidden`}
                  aria-label={
                    isRTL ? "قائمة الدورات التدريبية" : "Training courses menu"
                  }
                >
                  <div className="flex flex-col items-center gap-1">
                    <BookOpen className="h-5 w-5 transition-all duration-300 group-hover:scale-110 group-data-[state=open]:text-ecosafe-green" />
                    <span className="text-xs font-medium">
                      {isRTL ? "الدورات التدريبية" : "Training Courses"}
                    </span>
                  </div>
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div
                    className="p-6 w-[500px] bg-white/95 backdrop-blur-xl border border-ecosafe-green/20 shadow-2xl rounded-xl"
                    dir={isRTL ? "rtl" : "ltr"}
                  >
                    <div className="grid gap-4">
                      <div className="grid grid-cols-1 gap-3">
                        <Link
                          to="/training-courses"
                          className="group p-4 rounded-lg transition-all duration-300 border border-ecosafe-green/10 hover:border-ecosafe-green/30 hover:bg-ecosafe-green/5"
                        >
                          <h4 className="font-semibold text-ecosafe-black group-hover:text-ecosafe-green-dark transition-colors">
                            {isRTL ? "جميع الدورات" : "All Courses"}
                          </h4>
                          <p className="text-sm text-ecosafe-gray mt-1">
                            {isRTL
                              ? "تصفح كامل كتالوج دوراتنا التي تضم أكثر من 1200 دورة"
                              : "Browse our complete catalog of 1200+ courses"}
                          </p>
                        </Link>
                        <Link
                          to="/sectors"
                          className="group p-4 rounded-lg transition-all duration-300 border border-ecosafe-green/10 hover:border-ecosafe-green/30 hover:bg-ecosafe-green/5"
                        >
                          <h4 className="font-semibold text-ecosafe-black group-hover:text-ecosafe-green-dark transition-colors">
                            {isRTL ? "جميع الفئات" : "All Categories"}
                          </h4>
                          <p className="text-sm text-ecosafe-gray mt-1">
                            {isRTL
                              ? "اكتشف التدريب حسب فئات الصناعة"
                              : "Explore training by industry categories"}
                          </p>
                        </Link>
                      </div>
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Navigation items after Training Courses */}
            {navItemsAfterTraining.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;

              return (
                <Link key={item.key} to={item.href}>
                  <Button
                    variant="ghost"
                    className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all duration-300 hover:bg-ecosafe-green/10 group ${
                      isActive
                        ? "text-ecosafe-green-dark bg-ecosafe-green/5"
                        : `${getTextColorClasses()} hover:text-ecosafe-green-dark`
                    }`}
                    aria-label={`${isRTL ? "انتقل إلى" : "Navigate to"} ${
                      item.label
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 transition-all duration-300 group-hover:scale-110 ${
                        isActive ? "text-ecosafe-green" : ""
                      }`}
                    />
                    <span className="text-xs font-medium">{item.label}</span>
                  </Button>
                </Link>
              );
            })}
          </div>

          {/* Right Side Action Buttons */}
          <div
            className={`flex items-center flex-shrink-0 ${
              isRTL
                ? "space-x-reverse space-x-2 sm:space-x-3"
                : "space-x-2 sm:space-x-3"
            }`}
          >
            {/* Search Toggle Button */}
            <Button
              variant="ghost"
              size="icon"
              className={`hidden md:flex h-8 w-8 sm:h-10 sm:w-10 rounded-full transition-all duration-300 hover:scale-110 hover:bg-ecosafe-green/10 ${
                !isHomePage
                  ? "text-ecosafe-gray hover:text-ecosafe-green-dark"
                  : scrollState === 2
                  ? "text-ecosafe-gray hover:text-ecosafe-green-dark"
                  : "text-white hover:text-ecosafe-green-dark"
              }`}
              onClick={() => setShowSearch(!showSearch)}
              title={isRTL ? "بحث" : "Search"}
              aria-label={isRTL ? "فتح مربع البحث" : "Open search box"}
            >
              <Search className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>

            {/* Enhanced Language Switcher */}
            <div className="hidden sm:block">
              <div
                className={`p-1 backdrop-blur-lg rounded-lg shadow-lg border transition-all duration-300 ${
                  !isHomePage || scrollState === 2
                    ? "bg-ecosafe-green/50 border-ecosafe-green/70"
                    : "bg-white/10 border-white/20"
                }`}
              >
                <LanguageSwitcher />
              </div>
            </div>

            {/* Mobile Menu Toggle Button */}
            <Button
              variant="ghost"
              size="icon"
              className={`lg:hidden h-8 w-8 sm:h-10 sm:w-10 rounded-full hover:bg-ecosafe-green/10 transition-all duration-300 hover:scale-110 ${
                !isHomePage
                  ? "text-ecosafe-gray"
                  : scrollState === 2
                  ? "text-ecosafe-gray"
                  : "text-white"
              }`}
              onClick={() => setIsOpen(!isOpen)}
              title={isRTL ? "القائمة" : "Menu"}
              aria-label={
                isRTL
                  ? isOpen
                    ? "إغلاق القائمة"
                    : "فتح القائمة"
                  : isOpen
                  ? "Close menu"
                  : "Open menu"
              }
            >
              <div className="relative w-5 h-5 sm:w-6 sm:h-6">
                <span
                  className={`absolute top-1 left-0 w-5 sm:w-6 h-0.5 bg-current transform transition-all duration-300 ${
                    isOpen ? "rotate-45 translate-y-2" : ""
                  }`}
                ></span>
                <span
                  className={`absolute top-2.5 left-0 w-5 sm:w-6 h-0.5 bg-current transition-all duration-300 ${
                    isOpen ? "opacity-0" : ""
                  }`}
                ></span>
                <span
                  className={`absolute top-4 left-0 w-5 sm:w-6 h-0.5 bg-current transform transition-all duration-300 ${
                    isOpen ? "-rotate-45 -translate-y-2" : ""
                  }`}
                ></span>
              </div>
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        {showSearch && (
          <div className="hidden md:block py-4 border-t border-ecosafe-green/20">
            <div className="max-w-md mx-auto">
              <RealTimeSearch onClose={() => setShowSearch(false)} />
            </div>
          </div>
        )}

        {/* Mobile Navigation Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ease-in-out ${
            isOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          }`}
          dir={isRTL ? "rtl" : "ltr"}
        >
          <div className="py-4 space-y-2 border-t border-ecosafe-green/20 bg-white/95 backdrop-blur-xl rounded-b-xl shadow-xl">
            {/* Mobile Navigation Items */}
            {allNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;

              if (item.isDropdown) {
                return (
                  <DropdownMenu key={item.key}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full justify-between font-medium transition-all duration-300 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-ecosafe-black hover:text-ecosafe-green-dark hover:bg-ecosafe-green/10 text-sm sm:text-base"
                        aria-label={`${isRTL ? "فتح قائمة" : "Open"} ${
                          item.label
                        } ${isRTL ? "" : "menu"}`}
                      >
                        <div
                          className={`flex items-center ${
                            isRTL ? "flex-row-reverse" : ""
                          }`}
                        >
                          <Icon
                            className={`h-4 w-4 ${
                              isRTL ? "ml-2 sm:ml-3" : "mr-2 sm:mr-3"
                            }`}
                          />
                          {item.label}
                        </div>
                        <ChevronDown
                          className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`}
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-full bg-white/95 backdrop-blur-xl border border-ecosafe-green/20 shadow-xl">
                      <Link to="/training-courses">
                        <DropdownMenuItem className="font-medium text-ecosafe-black hover:text-ecosafe-green-dark hover:bg-ecosafe-green/10 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base">
                          {isRTL ? "جميع الدورات" : "All Courses"}
                        </DropdownMenuItem>
                      </Link>
                      <Link to="/sectors">
                        <DropdownMenuItem className="font-medium text-ecosafe-black hover:text-ecosafe-green-dark hover:bg-ecosafe-green/10 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base">
                          {isRTL ? "جميع الفئات" : "All Categories"}
                        </DropdownMenuItem>
                      </Link>
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              return (
                <Link key={item.key} to={item.href} className="block">
                  <Button
                    variant="ghost"
                    className={`w-full font-medium transition-all duration-300 px-3 sm:px-4 py-2 sm:py-3 rounded-lg hover:bg-ecosafe-green/10 text-sm sm:text-base ${
                      isRTL
                        ? "justify-start text-right flex-row-reverse"
                        : "justify-start text-left"
                    } ${
                      isActive
                        ? `text-ecosafe-green-dark bg-ecosafe-green/5 ${
                            isRTL ? "border-r-4" : "border-l-4"
                          } border-ecosafe-green`
                        : "text-ecosafe-black hover:text-ecosafe-green-dark"
                    }`}
                    aria-label={`${isRTL ? "انتقل إلى صفحة" : "Navigate to"} ${
                      item.label
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 ${
                        isRTL ? "ml-2 sm:ml-3" : "mr-2 sm:mr-3"
                      }`}
                    />
                    {item.label}
                  </Button>
                </Link>
              );
            })}

            {/* Mobile Language Switcher */}
            <div className="pt-3 sm:pt-4 px-2">
              <div className="p-2 bg-ecosafe-green/10 backdrop-blur-lg rounded-lg shadow-lg border border-ecosafe-green/30">
                <LanguageSwitcher />
              </div>
            </div>

            {/* Mobile Search */}
            <div className="pt-3 sm:pt-4 px-2">
              <RealTimeSearch isMobile={true} />
            </div>

            {/* Mobile Call-to-Action Button */}
            <div className="pt-3 sm:pt-4 px-2">
              <Button
                className="w-full rounded-full shadow-lg hover:scale-105 transition-all duration-300 bg-gradient-to-br from-ecosafe-green to-ecosafe-green-dark text-white font-bold py-2.5 sm:py-3 text-sm sm:text-base"
                aria-label={
                  isRTL
                    ? "ابدأ رحلتك التعليمية معنا"
                    : "Start your learning journey with us"
                }
              >
                {isRTL ? "ابدأ الآن" : "Get Started"}
              </Button>
            </div>

            {/* ECOSAFE Company Slogan */}
            <div className="pt-2 px-2 text-center pb-2">
              <p className="text-xs sm:text-sm font-medium italic text-ecosafe-green-dark">
                {isRTL
                  ? "تدرب لتقود، تعلم مع إيكوسيف"
                  : "Train to Lead, Learn with ECOSAFE."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navigation;
